import DOMPurify from 'dompurify';

export const allowedTagsDefault = ['b', 'sub', 'sup', 'hi', 'i', 'br', 'span', 'strong'];
export const allowedTagsExtendedDefault = [
  'p',
  'hi',
  'i',
  'br',
  'span',
  'b',
  'u',
  'strong',
  'em',
  'mark',
  'small',
  'del',
  'ins',
  'sub',
  'sup',
  'ul',
  'li',
  'table',
  'thead',
  'tbody',
  'tr',
  'th',
  'td',
];
export const tagsToRemoveDefault = ['script', 'style', 'iframe', 'link'];
export const allowedAttributesDefault = [];

/*
 * allowedTags will be rendered
 * tagsToRemove will be removed completely
 * all other tags will be escaped
 */
export function sanitizeEscape(
  input,
  { allowedTags = allowedTagsDefault, allowedAttributes = allowedAttributesDefault, tagsToRemove = tagsToRemoveDefault } = {}
) {
  // Add hook for escaping
  DOMPurify.addHook('afterSanitizeElements', function (node) {
    if ((node instanceof Element || node instanceof HTMLDocument) && !allowedTags.includes(node.tagName.toLowerCase())) {
      const newNode = document.createTextNode(node.outerHTML);
      node.parentNode.insertBefore(newNode, node);
      node.remove();
    }
  });

  const sanitized = DOMPurify.sanitize(input, {
    ADD_TAGS: allowedTags,
    FORBID_TAGS: tagsToRemove,
    ALLOWED_ATTR: allowedAttributes,
  });

  // Remove hook
  DOMPurify.removeHooks('afterSanitizeElements');

  return sanitized;
}

/*
 * allowedTags will be rendered
 * everything else will be escaped, including custom tag-like string, e.g. 3-<Difluor-methoxy>-benzoesaeure
 */
export function sanitizeEscapeCustom(input, { allowedTags = allowedTagsDefault } = {}) {
  // Make allowed tags array lower case
  const allowedTagsLowercase = allowedTags.map((c) => c.toLowerCase());

  // Output is the input, edited
  let output = input;

  // Attempt to match an opening or closing HTML tag
  const reg = /<\/?([a-zA_Z0-9]*)[^>]*?>/g;

  // An array that will contain all disallowed tags
  const disallowedTags = [];

  // For each tag in the input, if it's allowed, skip
  // Else, add it to the array.
  let match;
  // eslint-disable-next-line no-cond-assign
  while ((match = reg.exec(input)) !== null) {
    // eslint-disable-next-line no-continue
    if (allowedTagsLowercase.includes(match[1].toLowerCase())) continue;
    disallowedTags.push(match[0]);
  }

  // Replace each disallowed tag with the "escaped" version
  disallowedTags.forEach((tag) => {
    const find = tag;
    const replace = tag.replace(/</g, '&lt;').replace(/>/g, '&gt;');
    output = output.replace(find, replace);
  });

  return output;
}

/*
 * allowedTags will be rendered
 * all other tags will be discarded
 */
export function sanitizeDiscard(input, { allowedTags = allowedTagsDefault, allowedAttributes = allowedAttributesDefault } = {}) {
  return DOMPurify.sanitize(input, {
    ALLOWED_TAGS: allowedTags,
    ALLOWED_ATTR: allowedAttributes,
  });
}

export function sanitize(input, mode, options) {
  switch (mode) {
    case 'escape': {
      return sanitizeEscape(input, options);
    }
    case 'escapeCustom': {
      return sanitizeEscapeCustom(input, options);
    }
    case 'discard':
    default: {
      return sanitizeDiscard(input, options);
    }
  }
}
